import { Pipe, PipeTransform } from '@angular/core';

/**
 * Dates from the user service.
 *
 * The API writes UTC times without a zone ("2026-09-26T08:15:00"), which
 * JavaScript - and Angular's date pipe - would read as local time, putting
 * every time out by the viewer's offset (5½ hours in India). These read them
 * as the UTC they are.
 */
export function toServerDate(
  value: string | Date | null | undefined,
): Date | null {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;

  const text = String(value).trim();
  // Has a zone already (Z or +05:30), or is a bare date: leave it alone.
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(text);
  const hasTime = text.includes('T') || /\d{2}:\d{2}/.test(text);
  const parsed = new Date(hasZone || !hasTime ? text : `${text}Z`);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** One-time plans end on 31 Dec 9999 - "never", not a date. */
export function isNeverDate(value: string | Date | null | undefined): boolean {
  const date = toServerDate(value);
  return !!date && date.getUTCFullYear() >= 9999;
}

/**
 * The full date and time, in the viewer's zone and saying which it is - for
 * hover text on any shortened date. e.g. "Sat, 26 Sep 2026, 1:45:12 pm GMT+5:30".
 */
export function formatFullDateTime(
  value: string | Date | null | undefined,
): string {
  if (isNeverDate(value)) return 'Never expires';
  const date = toServerDate(value);
  if (!date) return '';

  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/** `{{ order.CreatedAt | serverDate | date: 'dd MMM yyyy' }}` */
@Pipe({ name: 'serverDate', standalone: true })
export class ServerDatePipe implements PipeTransform {
  transform(value: string | Date | null | undefined): Date | null {
    return toServerDate(value);
  }
}

/** `[matTooltip]="order.CreatedAt | fullDateTime"` */
@Pipe({ name: 'fullDateTime', standalone: true })
export class FullDateTimePipe implements PipeTransform {
  transform(value: string | Date | null | undefined): string {
    return formatFullDateTime(value);
  }
}
